// Notificações no celular (Web Push) e o app instalável. O service worker (public/sw.js) mostra o aviso e abre o
// jogo no toque. Sem as chaves no servidor, /api/push/chave devolve null e nada aparece.
// - O cartão de permissão aparece depois da 2ª partida terminada, nunca no primeiro acesso. "Agora não" volta 7 dias
//   depois.
// - No iPhone, o aviso só chega com o jogo na tela de início (iOS 16.4 ou mais novo): a tela explica como instalar.
import { api } from './api.ts';
import type { Sessao } from './conta.ts';
import { medir } from './medicao.ts';

export const TIPOS_DE_PUSH = ['chamado', 'amizade', 'ofensiva', 'liga', 'comunidade'] as const;
export type TipoPush = (typeof TIPOS_DE_PUSH)[number];
export const TEXTO_DO_TIPO: Record<TipoPush, { nome: string; quando: string }> = {
  chamado: { nome: 'Chamado para a mesa', quando: 'Quando um amigo chama você para a mesa. É o único que chega de madrugada.' },
  amizade: { nome: 'Pedido de amizade e convite de trio', quando: 'Quando alguém pede amizade ou chama você para um trio.' },
  ofensiva: { nome: 'Ofensiva', quando: 'Às 19h00, se você ainda não jogou no dia. Com 3 dias ou mais, também às 22h30.' },
  liga: { nome: 'Ranking da semana', quando: 'Domingo às 18h00, com a sua posição, e segunda às 10h00, quando a semana fecha.' },
  comunidade: { nome: 'Comunidade e campeonato', quando: 'Avisos das suas comunidades e dos campeonatos em que você está.' },
};
export const PERGUNTA = 'Quer saber quando a turma chamar e quando sua ofensiva estiver acabando?';
export const PARTIDAS_ANTES_DO_CARTAO = 2;
export const VOLTA_EM_MS = 7 * 24 * 3_600_000;
const AGORA_NAO = 'truco6:push-agora-nao';

export type EstadoDoPush =
  | 'sem_suporte' // navegador sem Web Push
  | 'instalar_iphone' // iPhone no Safari: só funciona com o jogo na tela de início
  | 'bloqueado' // a pessoa negou no navegador: só nas configurações do aparelho
  | 'ativo' // este aparelho recebe
  | 'inativo'; // pode ativar

export function ehIphone(ua = navigator.userAgent): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}
export function instalado(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
const suporta = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Registra o service worker ao abrir o jogo: é o que faz o app instalável e recebe o push.
export function registrarServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.MODE === 'test') return;
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => {}); }, { once: true });
}

async function inscricaoAtual(): Promise<PushSubscription | null> {
  if (!suporta()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function estadoDoPush(): Promise<EstadoDoPush> {
  if (ehIphone() && !instalado()) return 'instalar_iphone';
  if (!suporta()) return 'sem_suporte';
  if (Notification.permission === 'denied') return 'bloqueado';
  return Notification.permission === 'granted' && (await inscricaoAtual()) ? 'ativo' : 'inativo';
}

// A chave pública do servidor; null com o push desligado (sem as chaves VAPID).
let chave: Promise<string | null> | null = null;
export function chaveDoServidor(s: Sessao): Promise<string | null> {
  chave ??= api<{ chave: string | null }>(s, 'GET', '/push/chave').then((r) => r.chave, () => { chave = null; return null; });
  return chave;
}

function bytesDaChave(b64: string): Uint8Array<ArrayBuffer> {
  const bruto = atob((b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i++) out[i] = bruto.charCodeAt(i);
  return out;
}

// Pede a permissão do navegador, inscreve este aparelho e manda a inscrição para a API.
export async function ativarPush(s: Sessao, origem: 'cartao' | 'configuracoes'): Promise<EstadoDoPush> {
  const k = await chaveDoServidor(s);
  if (!k) throw new Error('As notificações fora do jogo estão desligadas por enquanto.');
  if (!suporta()) return 'sem_suporte';
  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') return permissao === 'denied' ? 'bloqueado' : 'inativo';
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytesDaChave(k) });
  const j = sub.toJSON();
  await api(s, 'POST', '/push/inscrever', { endpoint: j.endpoint, chaves: { p256dh: j.keys?.p256dh, auth: j.keys?.auth } });
  medir('push_permitido', { origem });
  return 'ativo';
}

export async function desativarPush(s: Sessao): Promise<void> {
  const sub = await inscricaoAtual();
  if (!sub) return;
  await api(s, 'POST', '/push/desinscrever', { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe().catch(() => false);
}

export const lerPreferencias = (s: Sessao) => api<{ ligado: boolean; tipos: Record<TipoPush, boolean> }>(s, 'GET', '/push/preferencias');
export const definirPreferencia = (s: Sessao, tipo: TipoPush, ligado: boolean) => api<{ tipos: Record<TipoPush, boolean> }>(s, 'POST', '/push/preferencias', { tipo, ligado });

// "Agora não": o cartão volta 7 dias depois.
export function agoraNao(agora = Date.now()): void {
  try { localStorage.setItem(AGORA_NAO, String(agora)); } catch { /* navegador sem armazenamento */ }
}
// O cartão aparece com 2 partidas terminadas ou mais, sem o push ativo e longe do último "Agora não".
export function mostrarCartao(partidas: number, estado: EstadoDoPush, agora = Date.now(), dispensadoEm = lerAgoraNao()): boolean {
  if (partidas < PARTIDAS_ANTES_DO_CARTAO) return false;
  if (estado !== 'inativo' && estado !== 'instalar_iphone') return false;
  return dispensadoEm === null || agora - dispensadoEm >= VOLTA_EM_MS;
}
function lerAgoraNao(): number | null {
  try { const v = Number(localStorage.getItem(AGORA_NAO)); return v > 0 ? v : null; } catch { return null; }
}

// O botão "Instalar" do Chrome e do Edge: o navegador avisa que dá para instalar, e o jogo guarda o pedido.
type PedidoDeInstalar = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let pedidoDeInstalar: PedidoDeInstalar | null = null;
const ouvintes = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (ev) => { ev.preventDefault(); pedidoDeInstalar = ev as PedidoDeInstalar; ouvintes.forEach((f) => f()); });
  window.addEventListener('appinstalled', () => { pedidoDeInstalar = null; medir('app_instalado'); ouvintes.forEach((f) => f()); });
}
export const podeInstalar = () => pedidoDeInstalar !== null;
export function aoMudarInstalar(f: () => void): () => void { ouvintes.add(f); return () => ouvintes.delete(f); }
export async function instalar(): Promise<boolean> {
  const p = pedidoDeInstalar;
  if (!p) return false;
  await p.prompt();
  const { outcome } = await p.userChoice;
  pedidoDeInstalar = null;
  ouvintes.forEach((f) => f());
  return outcome === 'accepted';
}
